import { Actor, Character } from "../../../types";
import { STAGE_CELL_SIZE } from "../../constants/constants";
import {
  getTransformedBounds,
  INVERSE_TRANSFORMS,
  pointApplyingTransform,
  transformSwapsDimensions,
} from "../../utils/stage-helpers";
import VariableOverlay from "../modal-paint/variable-overlay";
import { EMPTY_DRAG_IMAGE } from "../stage/stage";
import { DEFAULT_APPEARANCE_INFO, SPRITE_TRANSFORM_CSS } from "./sprite";

const MISSING_CHARACTER_QUESTION_MARK = `data:image/png;base64,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`;

/**
 * Calculate drag offsets for a sprite, accounting for CSS transforms.
 *
 * Returns two offsets:
 * - `forDrop`: The click position within the visual bounding box. Used for
 *   calculating drop position (the drop logic handles transforms separately).
 * - `forPreview`: The click position adjusted for transform-induced shifts.
 *   Used for the drag preview which needs layout-relative coordinates.
 *
 * CSS transforms with non-centered origins shift the visual bounding box
 * relative to the element's layout position. The preview needs this adjustment
 * to position sprites correctly.
 */
function calculateDragOffsets(
  event: React.DragEvent,
  rect: DOMRect,
  info: { width: number; height: number; anchor: { x: number; y: number } },
  transform: Actor["transform"],
): { forDrop: { x: number; y: number }; forPreview: { x: number; y: number } } {
  // Simple offset within the visual bounding box
  const forDrop = {
    x: event.clientX - rect.left,
    y: event.clientY - rect.top,
  };

  // Calculate how much the transform shifts the visual bounds from layout position
  const bounds = getTransformedBounds(info, transform);
  const transformOffsetX = (bounds.offsetX / bounds.width) * rect.width;
  const transformOffsetY = (bounds.offsetY / bounds.height) * rect.height;

  // Offset adjusted for transform shift (for preview positioning)
  const forPreview = {
    x: forDrop.x - transformOffsetX,
    y: forDrop.y - transformOffsetY,
  };

  return { forDrop, forPreview };
}

const ActorSprite = (props: {
  character: Character;
  actor: Actor;
  selected?: boolean;
  zIndex?: number;
  dragActorIds?: string[];
  onClick?: (e: React.MouseEvent) => void;
  onMouseUp?: (e: React.MouseEvent) => void;
  onDoubleClick?: (e: React.MouseEvent) => void;
  onStartDrag?: (
    actor: Actor,
    actorIds: string[],
    event: React.DragEvent,
    anchorOffset: { x: number; y: number },
  ) => void;
  transitionDuration?: number;
  skipTransition?: boolean;
}) => {
  const {
    actor,
    character,
    selected,
    zIndex,
    dragActorIds,
    transitionDuration: transitionDurationProp,
    skipTransition,
    onClick,
    onMouseUp,
    onDoubleClick,
    onStartDrag,
  } = props;

  const transitionDuration = skipTransition ? 0 : transitionDurationProp;

  if (!character) {
    return (
      <div
        title="This character has been removed and this rule will not execute."
        style={{
          position: "absolute",
          zIndex: selected ? 1000 : zIndex,
          transitionDuration: `${transitionDuration}ms`,
          left: (actor.position.x - 1) * STAGE_CELL_SIZE,
          // Y-up, 1-indexed: position.y measures from the bottom of the parent container.
          bottom: (actor.position.y - 1) * STAGE_CELL_SIZE,
          width: STAGE_CELL_SIZE,
          height: STAGE_CELL_SIZE,
          background: `url(${MISSING_CHARACTER_QUESTION_MARK})`,
        }}
      ></div>
    );
  }

  const { appearances, appearanceInfo } = character.spritesheet;
  const info = appearanceInfo?.[actor.appearance] || DEFAULT_APPEARANCE_INFO;

  const isEventInFilledSquare = (event: React.MouseEvent) => {
    if (!(event.target instanceof HTMLElement)) {
      return false;
    }
    // getBoundingClientRect() returns the visual bounds after CSS transforms AND
    // CSS zoom from the parent stage. We compute the visual size of one cell from
    // the element's actual rendered size so the math is correct at any zoom level.
    const { top, left, width, height } = event.target.getBoundingClientRect();
    const transform = actor.transform ?? "0";
    const swapped = transformSwapsDimensions(transform);
    const visualDims = swapped ? { width: info.height, height: info.width } : info;
    const cellWidth = width / visualDims.width;
    const cellHeight = height / visualDims.height;

    const [cellX, cellY] = pointApplyingTransform(
      Math.floor((event.clientX - left) / cellWidth),
      Math.floor((event.clientY - top) / cellHeight),
      visualDims,
      INVERSE_TRANSFORMS[transform],
    );
    if (info && !info.filled[`${cellX},${cellY}`]) {
      event.preventDefault();
      return false;
    }
    return true;
  };

  // Handle drag start - sets dataTransfer for external drop targets while using custom preview
  const onDragStart = (event: React.DragEvent) => {
    if (!dragActorIds) {
      event.preventDefault();
      return;
    }
    if (!(event.target instanceof HTMLElement)) {
      return;
    }
    if (!isEventInFilledSquare(event)) {
      return;
    }

    const rect = event.target.getBoundingClientRect();
    const offsets = calculateDragOffsets(event, rect, info, actor.transform);

    // Set dataTransfer data for external drop targets (variable boxes, etc.)
    event.dataTransfer.effectAllowed = "copyMove";
    event.dataTransfer.setData(
      "drag-offset",
      JSON.stringify({ dragLeft: offsets.forDrop.x, dragTop: offsets.forDrop.y }),
    );
    event.dataTransfer.setData(
      "sprite",
      JSON.stringify({
        dragAnchorActorId: actor.id,
        actorIds: dragActorIds,
      }),
    );

    // Hide native drag image - we use our custom preview instead
    event.dataTransfer.setDragImage(EMPTY_DRAG_IMAGE, 0, 0);

    // Start custom drag preview (uses transform-adjusted offset for correct positioning)
    onStartDrag?.(actor, dragActorIds, event, offsets.forPreview);
  };

  let data = new URL("../../img/splat.png", import.meta.url).href;
  if (appearances[actor.appearance]) {
    data = appearances[actor.appearance][0];
  }

  const variableOverlay = info.variableOverlay || { showVariables: false, visibleVariables: {} };
  const shouldShowVariables = Object.keys(variableOverlay.visibleVariables).some(
    (key) => variableOverlay.visibleVariables[key],
  );

  return (
    <div
      className="animated"
      style={{
        position: "absolute",
        zIndex: selected ? 1000 : zIndex,
        transitionDuration: `${transitionDuration}ms`,
        left: (actor.position.x - 1 - info.anchor.x) * STAGE_CELL_SIZE,
        // Y-up world (1-indexed): anchor pixel sits at world row position.y.
        // Sprite-pixel space stays Y-down (image top-left = (0,0)), so the
        // sprite's bottom edge is (info.height - 1 - anchor.y) cells below
        // the anchor cell's bottom edge at (position.y - 1) * cell.
        bottom: (actor.position.y + info.anchor.y - info.height) * STAGE_CELL_SIZE,
        pointerEvents: "none",
      }}
    >
      <img
        draggable={!!dragActorIds}
        data-stage-character-id={character.id}
        onDragStart={onDragStart}
        onMouseUp={(event) => {
          if (isEventInFilledSquare(event)) {
            onMouseUp?.(event);
          }
        }}
        onClick={(event) => {
          if (isEventInFilledSquare(event)) {
            onClick?.(event);
          }
        }}
        onDoubleClick={(event) => {
          if (isEventInFilledSquare(event)) {
            onDoubleClick?.(event);
          }
        }}
        src={data}
        className={`sprite ${selected ? "outlined" : ""}`}
        style={{
          transform: SPRITE_TRANSFORM_CSS[actor.transform ?? "0"],
          transformOrigin: `${((info.anchor.x + 0.5) / info.width) * 100}% ${((info.anchor.y + 0.5) / info.height) * 100}%`,
          transitionProperty: "transform",
          transitionDuration: `${transitionDuration}ms`,
          pointerEvents: "auto",
          cursor: dragActorIds ? "grab" : undefined,
        }}
      />
      {shouldShowVariables && (
        <VariableOverlay
          character={character}
          actor={actor}
          showVariables={true}
          visibleVariables={variableOverlay.visibleVariables}
          pixelSize={1}
          imageData={{
            width: info.width * STAGE_CELL_SIZE,
            height: info.height * STAGE_CELL_SIZE,
          }}
          stageContext={true}
        />
      )}
    </div>
  );
};

export default ActorSprite;
